import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { LogOut, Search, Menu, X, User } from 'lucide-react';
import { useAuth } from '../features/auth/AuthContext';
import { useEffect, useMemo, useState } from 'react';
import { DisclaimerGate } from '../components/legal/DisclaimerGate';
import { useAccountCredits } from '../features/account/useAccountCredits';

const browseLinks = [
  { to: '/crates', label: 'Crates' },
  { to: '/pricing', label: 'Pricing' }
];

const publicUtilityLinks = [
  { to: '/login', label: 'Sign In', primary: true }
];

const authedUtilityLinks = [
  { to: '/account', label: 'Account' }
];

export function AppShell() {
  const { user, loading: authLoading, logout } = useAuth() as any;
  const navigate = useNavigate();
  const location = useLocation();

  const [globalQuery, setGlobalQuery] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { creditState } = useAccountCredits(user?.id, authLoading);

  const isHomePage = location.pathname === '/';
  const canAccessCrates = !authLoading && Boolean(user);
  const showBrowseLinks = useMemo(
    () => browseLinks.filter((link) => link.to !== '/crates' || canAccessCrates),
    [canAccessCrates]
  );

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname, location.search]);

  function handleGlobalSearch(e: React.FormEvent) {
    e.preventDefault();
    const q = globalQuery.trim();
    if (!q) return;
    if (!canAccessCrates) {
      navigate(user ? '/pricing' : '/login');
      setGlobalQuery('');
      return;
    }
    navigate(`/crates?q=${encodeURIComponent(q)}`);
    setGlobalQuery('');
  }

  function closeAllMenus() {
    setMobileOpen(false);
    setUserMenuOpen(false);
  }

  return (
    <div className="relative min-h-screen overflow-x-hidden text-white">
      {isHomePage && (
        <div className="site-video-background" aria-hidden="true">
          <video
            className="site-video-background__media"
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
          >
            <source src="/img/redloop1.mp4" type="video/mp4" />
          </video>
          <div className="site-video-background__shade" />
        </div>
      )}

      <header className="sticky top-0 z-40 border-b border-white/10 bg-black/80 backdrop-blur-xl">
        <div className="container-shell flex h-20 items-center">
          {/* LEFT: Brand (logo = Home, watermark style, no text) */}
          <div className="flex items-center">
            <NavLink
              to="/"
              className="flex items-center cursor-pointer transition-all duration-300 hover:scale-110"
              onClick={closeAllMenus}
              aria-label="Home"
            >
              <img
                src="/img/logo-primetime2.png"
                alt="PrimeTime DJ Pool"
                className="h-46 w-auto object-contain opacity-80 drop-shadow-2xl translate-y-2.5"
              />
            </NavLink>
          </div>

          {/* CENTER: Browse nav (desktop) */}
          <nav className="mx-auto hidden items-center gap-8 xl:flex">
            {showBrowseLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                onClick={closeAllMenus}
                className={({ isActive }) =>
                  `nav-link whitespace-nowrap px-2.5 py-2 ${isActive ? 'nav-link-active' : ''}`
                }
              >
                {link.label}
              </NavLink>
            ))}
            {user && (
              <NavLink
                to="/library"
                onClick={closeAllMenus}
                className={({ isActive }) =>
                  `nav-link whitespace-nowrap px-2.5 py-2 ${isActive ? 'nav-link-active' : ''}`
                }
              >
                My Library
              </NavLink>
            )}
          </nav>

          {/* RIGHT: Utilities */}
          <div className="ml-auto flex items-center gap-3">
            <form onSubmit={handleGlobalSearch} className="hidden md:flex relative items-center ml-6 mr-2">
              <Search className="absolute left-3 text-white/50" size={14} />
              <input
                type="text"
                placeholder="Search catalog..."
                className="header-search-input w-44 py-1.5 pl-9 pr-3 lg:w-64"
                value={globalQuery}
                onChange={(e) => setGlobalQuery(e.target.value)}
              />
            </form>

            {user && (
              <div className="hidden rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 text-left md:block">
                <p className="text-[10px] font-black uppercase tracking-widest text-white/35">CREDITS</p>
                <p className="text-xs font-bold text-white/75" aria-live="polite">
                  {creditState.status === 'ready' && creditState.data
                    ? creditState.data.availableCredits
                    : creditState.status === 'loading'
                      ? '...'
                      : 'unavailable'}
                </p>
              </div>
            )}

            {/* Auth utilities */}
            {!user ? (
              <div className="hidden sm:flex items-center gap-3">
                {publicUtilityLinks.map((l) =>
                  l.primary ? (
                    <NavLink
                      key={l.to}
                      to={l.to}
                      onClick={closeAllMenus}
                      className="btn-primary rounded-xl px-6 py-2.5 whitespace-nowrap"
                    >
                      {l.label}
                    </NavLink>
                  ) : (
                    <NavLink 
                      key={l.to} 
                      to={l.to} 
                      onClick={closeAllMenus} 
                      className="nav-link px-2 py-1.5"
                    >
                      {l.label}
                    </NavLink>
                  )
                )}
              </div>
            ) : (
              <div className="relative hidden h-full sm:flex items-center">
                <button
                  type="button"
                  onClick={() => setUserMenuOpen((v) => !v)}
                  className={`header-icon-button gap-2 px-3 py-2 ${
                    location.pathname === '/account' ? 'nav-link-active text-prime-red' : 'text-white/80'
                  }`}
                  aria-label="Account menu"
                >
                  <User size={18} />
                  <span className="max-w-[12rem] truncate text-sm font-bold">My Account</span>
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 top-[calc(100%-0.5rem)] w-56 rounded-xl border border-white/10 bg-black/95 backdrop-blur-xl shadow-2xl animate-in fade-in slide-in-from-top-2 duration-200">
                    <div className="px-4 py-3 text-[10px] font-black uppercase tracking-widest text-white/30 border-b border-white/5">
                      Account Settings
                    </div>
                    <div className="p-1.5">
                      {authedUtilityLinks.map((l) => (
                        <NavLink
                          key={l.to}
                          to={l.to}
                          onClick={closeAllMenus}
                          className="nav-link flex items-center gap-2 px-3 py-2 text-sm"
                        >
                          {l.label}
                        </NavLink>
                      ))}

                      <button
                        type="button"
                        onClick={() => {
                          closeAllMenus();
                          if (typeof logout === 'function') logout();
                          else navigate('/login');
                        }}
                        className="focus-red mt-1 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-bold text-prime-red transition-all duration-200 ease-out hover:-translate-y-0.5 hover:bg-prime-red/10 active:translate-y-0 active:scale-[0.97] active:bg-prime-red/5"
                      >
                        <LogOut size={16} />
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Mobile menu button */}
            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              className="header-icon-button p-2 xl:hidden"
              aria-label="Open menu"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile dropdown */}
        {mobileOpen && (
          <div className="xl:hidden border-t border-white/10 bg-black/90 backdrop-blur-xl">
            <div className="container-shell py-4 space-y-3">
              {/* Search (mobile) */}
              <form onSubmit={handleGlobalSearch} className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50" size={14} />
                <input
                  type="text"
                  placeholder="Search catalog..."
                  className="header-search-input w-full py-2 pl-9 pr-3"
                  value={globalQuery}
                  onChange={(e) => setGlobalQuery(e.target.value)}
                />
              </form>

              {/* Browse links */}
              <div className="grid grid-cols-2 gap-2">
                {showBrowseLinks.map((link) => (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    onClick={closeAllMenus}
                    className={({ isActive }) =>
                      `nav-link border border-white/10 px-3 py-2 text-sm ${
                        isActive ? 'nav-link-active' : 'text-white/80'
                      }`
                    }
                  >
                    {link.label}
                  </NavLink>
                ))}
              </div>

              {/* Auth links */}
              {!user ? (
                <div className="flex gap-2">
                  <NavLink to="/login" onClick={closeAllMenus} className="nav-link px-3 py-2">
                    Login
                  </NavLink>
                  <NavLink to="/register" onClick={closeAllMenus} className="btn-primary px-3 py-2">
                    Sign Up
                  </NavLink>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <NavLink
                    to="/library"
                    onClick={closeAllMenus}
                    className="nav-link border border-white/10 px-3 py-2 text-sm text-white/80"
                  >
                    My Library
                  </NavLink>
                  {authedUtilityLinks.map((l) => (
                    <NavLink
                      key={l.to}
                      to={l.to}
                      onClick={closeAllMenus}
                      className="nav-link border border-white/10 px-3 py-2 text-sm text-white/80"
                    >
                      {l.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="relative z-10 min-h-[calc(100vh-8rem)]">
        <div key={location.pathname} className="page-route-shell">
          <Outlet />
        </div>
      </main>

      <footer className="relative z-10 border-t border-white/10 bg-black/45 py-2 text-center text-sm text-white/40 backdrop-blur-sm">
        <div className="flex flex-col items-center gap-1">
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
            <NavLink to="/for-labels" className="text-prime-red transition-colors hover:text-red-400 hover:underline">
              For Labels
            </NavLink>
            <span aria-hidden="true">·</span>
            <NavLink to="/dmca" className="text-prime-red transition-colors hover:text-red-400 hover:underline">
              Copyright &amp; DMCA
            </NavLink>
          </div>
          <p>
            Any questions? Contact us at{' '}
            <a href="mailto:support@primetimedjpool.com" className="text-prime-red transition-colors hover:text-red-400 hover:underline">
              support@primetimedjpool.com
            </a>
          </p>
          <p>© 2026 PrimeTime DJ Pool</p>
        </div>
      </footer>

      <DisclaimerGate />
    </div>
  );
}
